/**
 * Shared helpers for Admin Inventory Sync create (CSV semantics + UI session).
 * Mirrors backend inventory-sync quantity / Loose Item rules.
 */

export function isFractionalQuantity(value) {
  const n = Number(value)
  return Number.isFinite(n) && !Number.isInteger(n)
}

function hasMoreThanDecimalPlaces(value, maxDecimals = 2) {
  const str = String(value)
  const dot = str.indexOf('.')
  if (dot < 0) return false
  return str.length - dot - 1 > maxDecimals
}

/**
 * Validate Inventory Sync line quantity.
 * @param {unknown} value
 * @param {{ isLooseItem?: boolean }} [options]
 * @returns {{ ok: true, value: number, isLooseItem: boolean } | { ok: false, error: string }}
 */
export function validateInventorySyncQuantity(value, { isLooseItem = false } = {}) {
  if (value === '' || value === null || value === undefined) {
    return { ok: false, error: 'Quantity is required' }
  }
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return { ok: false, error: 'Quantity must be a positive number' }
  }
  if (hasMoreThanDecimalPlaces(numeric, 2)) {
    return {
      ok: false,
      error: 'Quantity supports up to 2 decimal places for Loose Items',
    }
  }

  const fractional = isFractionalQuantity(numeric)
  const treatAsLoose = !!isLooseItem || fractional
  if (!treatAsLoose && !Number.isInteger(numeric)) {
    return {
      ok: false,
      error: 'Quantity must be a whole number unless Loose Item is selected',
    }
  }

  const normalized = treatAsLoose
    ? Math.round((numeric + Number.EPSILON) * 100) / 100
    : numeric

  return {
    ok: true,
    value: normalized,
    isLooseItem: treatAsLoose,
  }
}

/**
 * Build submit item payload for admin Inventory Sync UI session.
 * Includes `new_product.is_loose_item` only when true (matches mobile/mini-web).
 */
export function buildInventorySyncSubmitItem(row) {
  const qtyResult = validateInventorySyncQuantity(row.quantity, {
    isLooseItem: !!(row.is_loose_item || row.new_product?.is_loose_item),
  })
  if (!qtyResult.ok) {
    throw new Error(qtyResult.error)
  }

  const base = {
    quantity: qtyResult.value,
    unit_cost: Number(row.unit_cost),
    unit_price: Number(row.unit_price),
    expiry_date: row.expiry_date || undefined,
    is_non_shwapno: !!row.is_non_shwapno,
  }

  if (row.admin_product_id) {
    return { ...base, admin_product_id: row.admin_product_id }
  }

  const newProduct = { ...(row.new_product || {}) }
  if (qtyResult.isLooseItem) {
    newProduct.is_loose_item = true
  } else {
    delete newProduct.is_loose_item
  }

  return { ...base, new_product: newProduct }
}
